import React, { useState, useContext } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { FileUploader } from "@/shared/components/file-uploader/upload-file";
import { useImportCsvExcelTrades } from "@/features/accounts/csv-excel/hooks/use-import-trades";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useInstitutionsContext } from "@/features/institutions/contexts/institutions-context";
import { OverlapInfo } from "@/features/accounts/ocr/services/post-trades-model";
import { DuplicateTradesModal } from "@/features/accounts/shared/components/duplicate-trades-modal";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { ErrorDialogContext } from "@/shared/context/error-context";
import { ConfirmDiscreteBalancesModal } from "@/features/accounts/csv-excel/components/confirm-discrete-balances-modal";


interface Props {
  csvExcelAccountId: string;
  passbookName?: string;
  onCloseModal: () => void;
  onCompleteModal: () => void;
}

export const UploadCsvExcelModal = ({
  csvExcelAccountId,
  passbookName,
  onCloseModal,
  onCompleteModal
}: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { importCsvExcelTrades, importMergeCsvExcelTrades } = useImportCsvExcelTrades();
  const { selectedAccountGroup } = useAccountGroupsContext();
  const { companyCode, setCompanyCode } = useInstitutionsContext();
  const { setError } = useContext(ErrorDialogContext);
  const { openHomeModal } = useModalContext();


  const [showType, setShowType] = useState<"" | "duplicate" | "discontinuity">("");
  const [overlapInfo, setOverlapInfo] = useState<OverlapInfo>();
  const [errorMessage, setErrorMessage] = useState<string>("");

  // アップロード開始ボタン押下時処理
  const onClickUpload = async () => {
    if (selectedFiles.length > 0) {
      // OCR明細取込処理を実装
      let type;
      let bankCode = undefined;
      if (openHomeModal === "UploadExcel" || openHomeModal === "FirstUploadExcel") {
        type = "excel";
      } else if (openHomeModal === "UploadCsv" || openHomeModal === "FirstUploadCsv") {
        type = "csv";
        bankCode = companyCode;
      } else {
        return;
      }
      const res = await importCsvExcelTrades(
        selectedAccountGroup.accountGroupId,
        csvExcelAccountId,
        selectedFiles[0],
        type,
        bankCode,
        passbookName,
      );
      if (res) {
        if (res.status === "success") {
          onCompleteModal();
          setCompanyCode("");
        } else if (res.status === "overlap") {
          setOverlapInfo(res.overlap_info);
          setShowType("duplicate");
        } else if (res.status === "discontinuity") {
          setErrorMessage(res.error_message);
          setShowType("discontinuity");
        } else {
          setError(new MoneyLinkWarning({
            title: "エラー",
            message: res.error_message || "不明なエラーが発生しました",
          }));
        }
      } else {
        setError(new MoneyLinkWarning({
          title: "エラー",
          message: "不明なエラーが発生しました",
        }));
      }
    }
  };

  // TODO: ボタンidの命名は再考の余地あり
  const handleImportMethodSelect = async (exclusionCondition: number) => {
    let type;
    let bankCode = undefined;
    if (openHomeModal === "UploadExcel" || openHomeModal === "FirstUploadExcel") {
      type = "excel";
    } else if (openHomeModal === "UploadCsv" || openHomeModal === "FirstUploadCsv") {
      type = "csv";
      bankCode = companyCode;
    } else {
      return;
    }
    const res = await importMergeCsvExcelTrades(
      selectedAccountGroup.accountGroupId,
      csvExcelAccountId,
      selectedFiles[0],
      type,
      bankCode,
      passbookName,
      2, // 残高不連続発生でも強制取込
      exclusionCondition
    );
    if (res) {
      if (res.status === "success") {
        onCompleteModal();
        setCompanyCode("");
      } else {
        setError(new MoneyLinkWarning({
          title: "エラー",
          message: res.error_message || "不明なエラーが発生しました",
        }));
      }
    } else {
      setError(new MoneyLinkWarning({
        title: "エラー",
        message: "不明なエラーが発生しました",
      }));
    }
    setShowType("");
  };

  const handleImportDiscreteBalances = async () => {
    let type;
    let bankCode = undefined;
    if (openHomeModal === "UploadExcel" || openHomeModal === "FirstUploadExcel") {
      type = "excel";
    } else if (openHomeModal === "UploadCsv" || openHomeModal === "FirstUploadCsv") {
      type = "csv";
      bankCode = companyCode;
    } else {
      return;
    }
    const res = await importMergeCsvExcelTrades(
      selectedAccountGroup.accountGroupId,
      csvExcelAccountId,
      selectedFiles[0],
      type,
      bankCode,
      passbookName,
      2, // 残高不連続発生でも強制取込
      0 // 期間重複未検証
    );
    if (res) {
      if (res.status === "success") {
        onCompleteModal();
        setCompanyCode("");
      } else if (res.status === "overlap") {
        setOverlapInfo(res.overlap_info);
        setShowType("duplicate");
      } else {
        setError(new MoneyLinkWarning({
          title: "エラー",
          message: res.error_message || "不明なエラーが発生しました",
        }));
      }
    } else {
      setError(new MoneyLinkWarning({
        title: "エラー",
        message: "不明なエラーが発生しました",
      }));
    }
  };

  return (
    <>
    {showType === "" && (
    <BasePageModal
      handleClose={onCloseModal}
      title="明細を追加"
      className="md-add-excel"
      bottomChildren={
        <>
          <BaseRoundButton
            buttonText="アップロード"
            id="upload-csv-excel-modal-001"
            loadingNegative={true}
            onClick={async () => {
              await onClickUpload();
            }}
            isActive={selectedFiles.length > 0}
          />
          {(openHomeModal === "FirstUploadExcel" || openHomeModal === "FirstUploadCsv") && (
          <BaseRoundButton
            buttonText="スキップ"
            id="upload-csv-excel-modal-002"
            loadingNegative={true}
            onClick={() => {
              onCompleteModal();
            }}
          />
          )}
        </>
      }
      disableOverlayClose={true}
    >
      <h2>読み込むファイルを選択</h2>
      <ul>
        <li>
          <FileUploader
            onFilesSelected={(files) => setSelectedFiles(files)}
            accept={openHomeModal === "UploadExcel" || openHomeModal === "FirstUploadExcel"
              ? "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              : openHomeModal === "UploadCsv" || openHomeModal === "FirstUploadCsv"
              ? "text/csv,application/csv"
              : "*/*"
            }
            multiple={false}
            maxFiles={1}
          />
        </li>
      </ul>
    </BasePageModal>
    )}
    {showType === "duplicate" && overlapInfo !== undefined && (
      <DuplicateTradesModal
        onClose={() => {
          setSelectedFiles([]);
          onCloseModal();
        }}
        onSelectMethod={(method) => handleImportMethodSelect(Number(method))}
        overlapInfo={overlapInfo}
      />
    )}
    {showType === "discontinuity" && (
      <ConfirmDiscreteBalancesModal
        errorMessage={errorMessage}
        upload={async () => {
          await handleImportDiscreteBalances();
        }}
        onCloseModal={() => {
          setSelectedFiles([]);
          setShowType("");
        }}
      />
    )}
    </>
  );
};
